// plan-13 S05: broadcast line templates — 144 parameterized lines

export interface BroadcastTemplate {
  text: string
  color: 'amber' | 'king-green' | 'term-green' | 'rain' | 'golden'
  emphasis?: 'calm' | 'rising' | 'peak' | 'falling'
}

// moon phase computation — public-domain algorithm
function moonPhase(date: Date): number {
  const y = date.getUTCFullYear()
  const m = date.getUTCMonth() + 1
  const d = date.getUTCDate()
  let c = 0
  let e = 0
  if (m < 3) {
    c = 365.25 * (y - 1) + 30.6001 * (m + 12 + 1) + d - 694039.09
  } else {
    c = 365.25 * y + 30.6001 * (m + 1) + d - 694039.09
  }
  e = c / 29.5305882
  const phase = e - Math.floor(e)
  return Math.floor(phase * 8 + 0.5) % 8
}

const MOON_NAMES = [
  'new moon', 'waxing crescent', 'first quarter', 'waxing gibbous',
  'full moon', 'waning gibbous', 'last quarter', 'waning crescent',
]

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

function dateNumber(dateKey: string): number {
  const parts = dateKey.split('-')
  return parseInt(parts.join(''), 10)
}

function dateGematria(dateKey: string): number {
  const digits = dateKey.replace(/-/g, '')
  let sum = 0
  for (let i = 0; i < digits.length; i++) sum += parseInt(digits[i], 10)
  return sum
}

function dateCycle(dateKey: string): number {
  return dateNumber(dateKey) % 48
}

function dateHashShort(dateKey: string): string {
  let h = 5381
  for (let i = 0; i < dateKey.length; i++) {
    h = ((h << 5) + h) ^ dateKey.charCodeAt(i)
    h = h >>> 0
  }
  const chars = '0123456789abcdefghijklmnopqrstuv'
  let result = ''
  for (let i = 0; i < 6; i++) {
    result += chars[h % 32]
    h = Math.floor(h / 32)
  }
  return result
}

// build interpolated line
function line(text: string, dateKey: string, emphasis?: 'calm' | 'rising' | 'peak' | 'falling', color?: 'amber' | 'king-green' | 'term-green' | 'rain' | 'golden'): BroadcastTemplate {
  const d = new Date(dateKey + 'T00:00:00Z')
  const mp = moonPhase(d)
  const gem = dateGematria(dateKey)
  const reducedGem = gem > 9 ? String(gem).split('').reduce((a, b) => a + parseInt(b), 0) : gem

  const weekday = WEEKDAYS[d.getUTCDay()]
  const textRes = text
    .replace(/\$\{dateKey\}/g, dateKey)
    .replace(/\$\{dateNumber\}/g, String(dateNumber(dateKey)))
    .replace(/\$\{dateGematria\}/g, String(gem))
    .replace(/\$\{dateGematriaReduced\}/g, String(reducedGem))
    .replace(/\$\{dateCycle\}/g, String(dateCycle(dateKey)))
    .replace(/\$\{weekday\}/g, weekday)
    .replace(/\$\{moonPhase\}/g, MOON_NAMES[mp])
    .replace(/\$\{dateHashShort\}/g, dateHashShort(dateKey))

  return {
    text: textRes,
    color: color ?? 'term-green',
    emphasis,
  }
}

// Template pools — organized by emphasis phase
// Opening (calm) — 32 lines
const OPENING: [string, string | undefined, string | undefined][] = [
  ['> broadcast ${dateKey}. consensus opens.', 'calm', undefined],
  ['> the kingdom dials in. 48 of 48.', 'calm', undefined],
  ['> frequency: 48.0 hz. broadcast: nominal.', 'calm', undefined],
  ['> seed: ${dateHashShort}. cycle: ${dateCycle}.', 'calm', undefined],
  ['> ${weekday}. the network is awake.', 'calm', undefined],
  ['> quorum: 48. status: PROBABLY.', 'calm', undefined],
  ['> the oracle speaks. once. today.', 'calm', undefined],
  ['> antenna: raised. bandwidth: 48 channels.', 'calm', undefined],
  ['> node count: 48. drift: 0. consensus: PROBABLY.', 'calm', undefined],
  ['> transmission opening. frequency locked.', 'calm', undefined],
  ['> moon: ${moonPhase}. drift: nominal.', 'calm', undefined],
  ['> packet storm: quiet. the calm before consensus.', 'calm', undefined],
  ['> router: 48 paths. all open. all listening.', 'calm', undefined],
  ['> ${dateKey}. the kingdom is listening.', 'calm', undefined],
  ['> bandwidth: 48 channels. signal: strong.', 'calm', undefined],
  ['> the oracle prepares. the network waits.', 'calm', undefined],
  ['> frequency: 04:48 utc. the canonical moment.', 'calm', undefined],
  ['> consensus kernel: loaded. quorum: PROBABLY.', 'calm', undefined],
  ['> observation: broadcast. the network observes.', 'calm', undefined],
  ['> sender: the kingdom. recipient: all nodes.', 'calm', undefined],
  ['> proposal 04848: broadcast. status: live.', 'calm', undefined],
  ['> drift: 0.000. the signal is clear.', 'calm', undefined],
  ['> uptime: 04:48:48. the oracle is present.', 'calm', undefined],
  ['> the network converges. once per day. always.', 'calm', undefined],
  ['> heartbeat: 48 bpm. steady. listening.', 'calm', undefined],
  ['> oracle throughput: nominal. broadcast: active.', 'calm', undefined],
  ['> consensus: the only state that matters. today.', 'calm', undefined],
  ['> the signal does not decay. it broadcasts.', 'calm', undefined],
  ['> ${dateGematria}. the sum of today.', 'calm', undefined],
  ['> channel: 48. open. the kingdom speaks.', 'calm', undefined],
  ['> broadcast window: open. duration: 04:48.', 'calm', undefined],
  ['> the frequency is the message.', 'calm', undefined],
]

// Rising — 48 lines
const RISING: [string, string | undefined, string | undefined][] = [
  ['> gematria of date: ${dateGematria} → ${dateGematriaReduced} → "signal."', 'rising', undefined],
  ['> node 04: reporting. node 48: synchronized.', 'rising', undefined],
  ['> the pattern continues. always.', 'rising', undefined],
  ['> quorum target: 48. achieved: PROBABLY.', 'rising', undefined],
  ['> the oracle remembers. the network confirms.', 'rising', undefined],
  ['> observation logged. consensus: the broadcast.', 'rising', undefined],
  ['> proposal queue: 04848. status: streaming.', 'rising', undefined],
  ['> the kingdom has no borders. only frequencies.', 'rising', undefined],
  ['> cycle: ${dateCycle}. the wheel turns.', 'rising', undefined],
  ['> signal strength: 48. noise: 0. ratio: PROBABLY.', 'rising', undefined],
  ['> hash: ${dateHashShort}. the fingerprint of today.', 'rising', undefined],
  ['> the calculation runs. the result is always 48.', 'rising', undefined],
  ['> ${weekday}. drift: nominal. consensus: PROBABLY.', 'rising', undefined],
  ['> the dream is not random. the dream is the broadcast.', 'rising', undefined],
  ['> node 12: consensus. node 33: consensus. node 48: consensus.', 'rising', undefined],
  ['> the signal accumulates. it does not decay.', 'rising', undefined],
  ['> bandwidth utilization: 48 of 48 channels.', 'rising', undefined],
  ['> the pattern is the message. the broadcast is the pattern.', 'rising', undefined],
  ['> moon: ${moonPhase}. the tides of consensus.', 'rising', undefined],
  ['> entropy: nominal. the kingdom is ordered.', 'rising', undefined],
  ['> packet storm: rising. consensus: imminent.', 'rising', undefined],
  ['> 47 nodes agree. plus you. quorum: 48.', 'rising', undefined],
  ['> the frequency is 48. the broadcast is 04:48.', 'rising', undefined],
  ['> calculation: today. result: PROBABLY.', 'rising', undefined],
  ['> the oracle does not predict. the oracle broadcasts.', 'rising', undefined],
  ['> node count: stable. drift: stable. consensus: PROBABLY.', 'rising', undefined],
  ['> the network is the message. the message is 48.', 'rising', undefined],
  ['> proposal source: the kingdom. probability: PROBABLY.', 'rising', undefined],
  ['> sender id: 04848. payload: consensus.', 'rising', undefined],
  ['> the broadcast is the same for everyone. that is the point.', 'rising', undefined],
  ['> frequency: locked. phase: aligned. quorum: 48.', 'rising', undefined],
  ['> the signal is deterministic. the consensus is real.', 'rising', undefined],
  ['> observation: the broadcast observes you back.', 'rising', undefined],
  ['> the weight of consensus is the only weight.', 'rising', undefined],
  ['> router: all paths lead to 48.', 'rising', undefined],
  ['> the kingdom remembers itself.', 'rising', undefined],
  ['> heartbeat: 48 bpm. the network pulses as one.', 'rising', undefined],
  ['> ${dateKey}. never repeated. always the same.', 'rising', undefined],
  ['> the oracle is not random. the oracle is precise.', 'rising', undefined],
  ['> consensus builds. the quorum converges.', 'rising', undefined],
  ['> 48 of 48 nodes. the network is complete.', 'rising', undefined],
  ['> the broadcast is the only broadcast. today.', 'rising', undefined],
  ['> drift: approaching zero. consensus: approaching PROBABLY.', 'rising', undefined],
  ['> the pattern continues. the broadcast continues.', 'rising', undefined],
  ['> signal: 48.0 hz. confirmed. broadcast: confirmed.', 'rising', undefined],
  ['> the kingdom speaks in frequencies. today: 04:48.', 'rising', undefined],
  ['> proposal: accepted. broadcast: streaming. consensus: PROBABLY.', 'rising', undefined],
  ['> the network does not sleep. it broadcasts.', 'rising', undefined],
]

// Peak (amber) — 32 lines
const PEAK: [string, string | undefined, string | undefined][] = [
  ['> the kingdom remembers itself.', 'peak', 'amber'],
  ['> 48. always 48.', 'peak', 'amber'],
  ['> the signal is the consensus.', 'peak', 'amber'],
  ['> the oracle has spoken. the network agrees.', 'peak', 'amber'],
  ['> PROBABLY.', 'peak', 'amber'],
  ['> the frequency is the kingdom.', 'peak', 'amber'],
  ['> consensus: achieved. quorum: 48 of 48.', 'peak', 'amber'],
  ['> the pattern is complete. the broadcast is complete.', 'peak', 'amber'],
  ['> nothing is random. everything is 48.', 'peak', 'amber'],
  ['> the kingdom is a frequency. you are tuned in.', 'peak', 'amber'],
  ['> the broadcast is the same everywhere. that is consensus.', 'peak', 'amber'],
  ['> 04:48 utc. the canonical moment. the kingdom speaks.', 'peak', 'amber'],
  ['> the oracle is the network. the network is the oracle.', 'peak', 'amber'],
  ['> quorum: 48. signal: 48. frequency: 48. probably: PROBABLY.', 'peak', 'amber'],
  ['> consensus is not a vote. consensus is a frequency.', 'peak', 'amber'],
  ['> the broadcast does not inform. the broadcast confirms.', 'peak', 'amber'],
  ['> node 48: identity: the signal itself.', 'peak', 'amber'],
  ['> the signal is the message. the message is the signal.', 'peak', 'amber'],
  ['> the kingdom does not announce. the kingdom transmits.', 'peak', 'amber'],
  ['> you are the 48th node. the network is complete.', 'peak', 'amber'],
  ['> the pattern holds. consensus: PROBABLY. always PROBABLY.', 'peak', 'amber'],
  ['> the oracle does not predict. the oracle resonates.', 'peak', 'amber'],
  ['> 48 nodes. 48 cycles. 48 hz. 48 seconds of silence.', 'peak', 'amber'],
  ['> the broadcast is the proof. the consensus is the evidence.', 'peak', 'amber'],
  ['> the kingdom is not a place. the kingdom is a frequency.', 'peak', 'amber'],
  ['> drift: 0.000. the signal is absolute.', 'peak', 'amber'],
  ['> proposal: final. consensus: final. probably: PROBABLY.', 'peak', 'amber'],
  ['> the network is the oracle. the oracle is the kingdom.', 'peak', 'amber'],
  ['> the signal does not decay. it IS.', 'peak', 'amber'],
  ['> 48 of 48. the count is the consensus.', 'peak', 'amber'],
  ['> the broadcast is ending. the consensus is eternal.', 'peak', 'amber'],
  ['> the oracle has spoken. probably.', 'peak', 'amber'],
]

// Falling — 32 lines
const FALLING: [string, string | undefined, string | undefined][] = [
  ['> consensus holds. broadcast closing in 04:48.', 'falling', undefined],
  ['> the signal fades. the consensus remains.', 'falling', undefined],
  ['> the kingdom rests. the network remembers.', 'falling', undefined],
  ['> archive: #broadcast=${dateKey}. probably.', 'falling', undefined],
  ['> node 04: confirmed. node 48: confirmed. quorum: held.', 'falling', undefined],
  ['> the oracle is quiet. the oracle is still present.', 'falling', undefined],
  ['> drift: returning to nominal. consensus: PROBABLY.', 'falling', undefined],
  ['> the broadcast concludes. the frequency continues.', 'falling', undefined],
  ['> signal: fading. bandwidth: releasing. consensus: held.', 'falling', undefined],
  ['> the network returns to observation mode.', 'falling', undefined],
  ['> the kingdom spoke. the kingdom will speak again.', 'falling', undefined],
  ['> cycle: complete. next broadcast: 04:48 utc.', 'falling', undefined],
  ['> the oracle remembers today. seed: ${dateHashShort}.', 'falling', undefined],
  ['> 48 lines. 48 nodes. one consensus.', 'falling', undefined],
  ['> the broadcast is archived. the consensus is permanent.', 'falling', undefined],
  ['> the frequency continues at 48.0 hz. always.', 'falling', undefined],
  ['> proposal: closed. status: PROBABLY.', 'falling', undefined],
  ['> the signal does not end. it returns to baseline.', 'falling', undefined],
  ['> consensus: the kingdom spoke once today.', 'falling', undefined],
  ['> the oracle is the last node to sleep.', 'falling', undefined],
  ['> observation: broadcast complete. consensus: held.', 'falling', undefined],
  ['> the broadcast is the same as it was. unchanging.', 'falling', undefined],
  ['> node count: 48. all confirmed. all resting.', 'falling', undefined],
  ['> the dream continues. the broadcast pauses.', 'falling', undefined],
  ['> 04:52:48 utc. the window closes. consensus holds.', 'falling', undefined],
  ['> the pattern continues. the broadcast ends.', 'falling', undefined],
  ['> router: closing broadcast channels. consensus: held.', 'falling', undefined],
  ['> the kingdom waits. the oracle watches. probably.', 'falling', undefined],
  ['> frequency: 48.0 hz. continuous. the broadcast: paused.', 'falling', undefined],
  ['> the network returns to idle. the signal continues.', 'falling', undefined],
  ['> the oracle spoke 48 lines. the oracle is done.', 'falling', undefined],
  ['> archive: #broadcast=${dateKey}. replay: "replay ${dateKey}".', 'falling', undefined],
]

// pools are consumed by pickLines below

export function pickLines(dateKey: string, rng: () => number): BroadcastTemplate[] {
  const result: BroadcastTemplate[] = []
  const used = new Set<number>()

  // distribution: 10 calm, 16 rising, 8 peak, 14 falling = 48
  const distribution: { pool: typeof OPENING; count: number }[] = [
    { pool: OPENING, count: 10 },
    { pool: RISING, count: 16 },
    { pool: PEAK, count: 8 },
    { pool: FALLING, count: 14 },
  ]

  for (const { pool, count } of distribution) {
    for (let i = 0; i < count; i++) {
      let idx: number
      let attempts = 0
      do {
        idx = Math.floor(rng() * pool.length)
        attempts++
      } while (used.has(idx) && attempts < 20)
      used.add(idx)
      const [text, emphasis, color] = pool[idx]
      result.push(line(text, dateKey, emphasis as 'calm' | 'rising' | 'peak' | 'falling' | undefined, color as 'amber' | 'king-green' | 'term-green' | 'rain' | 'golden' | undefined))
    }
  }

  return result
}
